import {erLikeObjekter} from "felles-ng2/støttefunksjoner/objekter";
import {isArray, isEmpty} from "radash";
import {erIkkeNil, erNil, standard} from "felles-ng2/støttefunksjoner/nilsjekk";
import {nil} from "felles-ng2/typer/nullish";
import {erLikeSet} from "felles-ng2/støttefunksjoner/set";
import {EnEllerFlere} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellHendelseHåndteringBygger";

/** @deprecated bruk erListe */
export function erList(muligListe: any): muligListe is any[] {
    return isArray(muligListe);
}

export function erListe(muligListe: any): muligListe is any[] {
    return isArray(muligListe);
}

export function erIkkeListe(muligListe: any): boolean {
    return !isArray(muligListe);
}

/** Sann dersom angitt liste er tom eller nil */
export function erTomListe<T>(liste: T[] | nil): boolean {
    return isEmpty(liste)
}

/** Sann dersom angitt liste er en IKKE-tom listen (array) */
export function erIkkeTomListe<T>(liste: T[] | nil): liste is T[] {
    return !erTomListe(liste) && erListe(liste)
}

/** Returnerer sann dersom listene er like.
 * Merk at vi gjør grunn sammenlikning, hvert innslag i listene sammenliknes vhja {@link erLikeObjekter}. */
export function erLikeLister<T extends Object>(a: T[] | null, b: T[] | null): boolean {
    if (erNil(a) && erNil(b)) return true;
    if (erNil(a) || erNil(b)) return false;

    if (a.length != b.length) return false;
    for (let i = 0; i < a.length; i++)
        if (!erLikeObjekter(a[i], b[i])) return false;
    return true;
}

/** Returnerer sann dersom listene er like uten hensyn til rekkefølge på innslagene.
 * MERK at denne metoden kun gir gyldig resultat for lister med primitiver da listene gjøres om til Set og så sammenliknes vhja {@link erLikeSet}. */
export function erLikeListerIgnorerRekkefølge<T extends Object>(a: T[] | null, b: T[] | null): boolean {
    if (erNil(a) && erNil(b)) return true;
    if (erNil(a) || erNil(b)) return false;

    if (a.length != b.length) return false;

    return erLikeSet(new Set(a), new Set(b));
}

/** Returnerer angitt element/elementer som liste */
export function somListe<T>(verdi: EnEllerFlere<T>): T[] {
    return erListe(verdi) ? verdi : [verdi];
}

/** Returnerer angitt liste derom denne er ikke-null, eller en tom liste */
export function nullSikkerListe<T>(liste: T[] | nil): T[] {
    return standard(liste, []);
}

/** Returnerer siste innslag i angitt liste, eller null dersom listen er tom eller null */
export function siste<T>(liste: T[]): T | null {
    return erIkkeTomListe(liste) ? liste[liste.length - 1] : null;
}

/** Returnerer siste innslag i angitt liste som matcher angitt filter, eller null dersom ingen funnet */
export function sisteSomMatcher<T>(liste: T[], filter: (kandidat: T) => boolean): T | null {
    return siste(liste.filter(filter) ?? []);
}

/** Returnerer første innslag i angitt liste, eller null dersom listen er tom eller null */
export function første<T>(liste: T[]): T | null {
    return erIkkeTomListe(liste) ? liste[0] : null;
}

/** Returnerer ny Array men angitt lengde, fylt med null-verdier */
export function nyArray(lengde: number) {
    return new Array(lengde).fill(null);
}

/** Returnerer innslag på angitt indeks, null dersom indeks ikke er gyldig */
export function innslagPåIndeks<T>(samling: T[], indeks: number): T | null {
    if (indeks < 0 || indeks > samling.length - 1) return null;
    return samling[indeks];
}

export function antallInnslagIListe(liste: any[] | null) {
    return liste?.length ?? 0;
}

/** Returnerer kopi av angitt 'elementer' der angitt 'element' er lagt til på slutten dersom element ikke allerede finnes i elementer.
 * Angitt erLik benyttes for å se om noen av de eksisterende elementene angitt elementer er lik angitt element. */
export function leggTilListeDersomIkkeInkludert<T>(element: T | nil, elementer: T[], erLik: (a: T, b: T) => boolean): T[] {
    if (erNil(element)) return elementer;
    if (!elementer.some(eksisterende => erLik(eksisterende, element)))
        return [...elementer, element];
    return elementer;
}

/** Fjerner elementer fra angitt liste som matcher angitt kriterie, returnerer en kopi av listen. */
export function fjernFraListe<T>(liste: T[], kriterie: (innslag: T) => boolean): T[] {
    return liste.filter(innslag => !kriterie(innslag));
}

/** Fjerner elementer fra angitt liste som matcher angitt kriterie, muterer angitt liste.
 * Returnerer antall elementer fjernet. */
export function fjernFraListeInline<T>(liste: T[], kriterie: (innslag: T) => boolean): number {
    const skalFjernes = liste.filter(innslag => kriterie(innslag));
    skalFjernes.forEach(innslag => liste.splice(liste.indexOf(innslag), 1));
    return skalFjernes.length;
}

/** Returnerer liste som inneholder alle elementene som finnes i alle angitte lister */
export function interseksjon<T>(...lister: T[][]): T[] {
    return lister.reduce((a, b) => a.filter(c => b.includes(c)));
}

/** Returnerer liste som inneholder alle elementene som ikke er i alle angitte lister */
export function disjunksjon<T>(...lister: T[][]): T[] {
    const elementerSomFinnesIAlle = new Set(interseksjon(...lister));
    const alleElementer = new Set(lister.reduce((a, b) => [...a, ...b]));
    return [...alleElementer].filter(element => !elementerSomFinnesIAlle.has(element))
}

/** Returnerer sann dersom interseksjonen av angitte lister ikke er tom */
export function erOverlappendeLister<T>(...lister: T[][]): boolean {
    return interseksjon(...lister).length > 0;
}

/** Returnerer unike verdier i angitt liste, unikhet bestemmes av angitt komparator */
export function distinkte<T>(liste:T[], komparator:(a:T, b:T)=>boolean = (a, b) => a===b):T[]{
    return liste.reduce((distinkte, kandidat )=>
      [...distinkte, ...(distinkte.some(eksisterende => komparator(eksisterende, kandidat)) ? [] : [kandidat])],
      [] as T[])
}

// map/filter/reduce funksjoner

/** Metode for å inspisere element i en listen i en map-operasjon uten å endre innslaget.
 * Bruk: [].map(inspiser(innslag => console.log('element i liste', innslag))) */
export function inspiser<T>(metode: (innslag: T) => void): (innslag: T) => T {
    return (innslag: T) => {
        try {
            metode(innslag);
        } catch (e) {
            console.error(e);
        }
        return innslag;
    }
}

/** Filter som kun returnerer første innslag i liste.
 * Bruk: [..].filter(filtrerTilFørsteInnslag())*/
export function filtrerTilFørsteInnslag() {
    return (innslag: any, indeks: number) => indeks == 0;
}

/** Filter bort null-innslag i liste.
 * Bruk: [..].filter(filtrerBortNullInnslag())*/
export function filtrerBortNullInnslag() {
    return (innslag: any) => erIkkeNil(innslag);
}

export function mapBasertPåIndeks<T>(mapFunksjon: (indeks: number) => T) {
    return (innslag: any, indeks: number) => mapFunksjon(indeks);
}

/** Reduserer til første innslag.
 * Forutsetter at det finnes minst ett element i listen som reduce kalles.
 * Bruk: [..].reduce(reduserTilFørsteInnslag()) */
export function reduserTilFørsteInnslag<T>() {
    return (forrige: T) => {
        return forrige;
    }
}

/** Reduserer til første innslag i listen, angitt standardVerdi dersom listen er tom.
 * Bruk: [..].reduce(...reduserTilFørsteInnslagEller(<en verdi>)) */
export function reduserTilFørsteInnslagEller<T>(standardVerdi: T): [reducer: (forrige: T, denne: T, dennesIndex: number, alle: T[]) => T, dersomTom: T] {
    const reduser = (forrige: T, denne: T, dennesIndex: number) => {
        return dennesIndex == 0 ? denne : forrige;
    };
    return [reduser, standardVerdi];
}

/** Reduserer til første innslag i listen, null dersom listen er tom.
 * Bruk: [..].reduce(...reduserTilFørsteInnslagEllerNull()) */
export function reduserTilFørsteInnslagEllerNull<T>(): [reducer: (forrige: T, denne: T, dennesIndex: number, alle: T[]) => T, dersomNull: T] {
    const reduser = (forrige: T, denne: T, dennesIndex: number, alle: T[]) => {
        return dennesIndex == 0 ? denne : forrige;
    };
    return [reduser, null as T];
}

/** Reduserer til største tall i liste med tall.
 * Bruk: [..].reduce(...reduserTilMaks()) */
export function reduserTilMaks(): [reducer: (forrige: number, denne: number, dennesIndex: number, alle: number[]) => number, dersomNull: number] {
    const reduser = (forrige: number, denne: number) => Math.max(forrige, denne);
    return [reduser, 0];
}

/** Reduserer til minste tall i liste med tall.
 * Bruk: [..].reduce(...reduserTilMin()) */
export function reduserTilMin(): [reducer: (forrige: number, denne: number, dennesIndex: number, alle: number[]) => number, dersomNull: number] {
    const reduser = (forrige: number, denne: number) => Math.min(forrige, denne);
    return [reduser, Number.MAX_SAFE_INTEGER];
}

/** Reduserer til summen av alle innslag i listen, 0 dersom listen er tom.
 * Bruk: [..].reduce(...reduserTilSum()) */
export function reduserTilSum(): [reducer: (forrige: number, denne: number, dennesIndex: number, alle: number[]) => number, 0] {
    const reduser = (sum: number, denne: number) => sum + denne;
    return [reduser, 0];
}
